import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { storePortalEmail } from '../../../lib/portalSignIn';
import { requestPortalSignInLink } from './portalApi';
import { KYCK_LOGO_URL } from './PortalLockup';
import { PortalShell } from './PortalShell';

// CLIENT PORTAL — the signed-out page.
//
// ⚠️⚠️ THE CONFIRMATION IS DELIBERATELY VAGUE, AND MUST STAY THAT WAY. It says
// "if that address has access, we've sent a link" because the server answers
// identically whether or not it does — and a friendlier "we've sent you a link!"
// would put back on the screen exactly the fact the endpoint refuses to leak.
// Which addresses are our clients is commercially sensitive: "does Kyck work
// with this dealership" is a competitor's question, and a login box that
// answers it is a leak with no data breach attached.
//
// The wording is chosen so the real recipient is not confused by it — they know
// they have access, so "if that address has access" reads as ordinary caution
// rather than doubt.

type Props = {
  /** Surfaced above the form when a sign-in link failed, so the retry has context. */
  notice?: string | null;
};

export function PortalSignIn({ notice }: Props) {
  const [email, setEmail] = useState('');

  const mut = useMutation({
    mutationFn: async (address: string) => {
      // Remembered BEFORE the request, so the finish page has it even if the
      // user closes the tab — Firebase needs the address to complete the link.
      storePortalEmail(address);
      await requestPortalSignInLink(address);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (trimmed) mut.mutate(trimmed);
  };

  return (
    // ⚠️ THE PAGE OWNS THE LOCKUP HERE, so the chrome renders none — see
    // `chromeLockup`. Two on one screen is how this portal came to have two looks.
    <PortalShell chromeLockup={false}>
      <div className="mx-auto max-w-md">
        {/*
          ⚠️⚠️ THE REAL LOGO FILE, NEVER "kyck" SET IN TYPE (rule 12). The shared
          `KYCK_LOGO_URL`, not a second copy of the path — one constant, so a future
          rename cannot leave this page pointing at a file that has moved.

          ⚠️⚠️ ON AN INK-DEEP PANEL, WHICH IS WHAT MAKES ONE WHITE MARK CORRECT IN
          BOTH MODES. The page ground follows the reader's mode, so a white logo on
          it would be invisible in light — and the alternative is a second artwork
          plus a mode switch to choose between them. `.psh-top` already solves this
          the same way, and its own note records why.
        */}
        <div className="psh-door">
          <img src={KYCK_LOGO_URL} alt="Kyck" className="psh-door-logo" />
          {/* ⚠️ SENTENCE CASE (rule 11). */}
          <p className="psh-door-title">Client portal</p>
          {/*
            ⚠️⚠️ ONE CONCRETE LINE, AND NO CADENCE CLAIM. "Updated daily" and its
            relatives are a figure with no source — the report carries its own
            provenance lines, and static copy cannot be right about freshness.

            ⚠️ AND NOTHING ELSE. No stats, no testimonials, no client logos: it is a
            door, not a brochure, and a login page that sells reads as a marketing
            page that lost its login. A figure here is also one more place for a
            number to go stale unnoticed, away from `facts.md`.
          */}
          <p className="psh-door-line">Reports, assets and campaigns for your clubs.</p>
        </div>

        <h1 className="mt-8 text-lg font-semibold tracking-tight text-foreground">Sign in</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter your email address and we'll send you a link to sign in. There's no password to
          remember.
        </p>

        {notice && (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {notice}
          </p>
        )}

        {mut.isSuccess ? (
          <div className="mt-6 rounded-md border border-border bg-card p-5">
            <p className="text-sm text-foreground">
              If that email has portal access, we've sent a sign-in link to{' '}
              <span className="font-medium">{email.trim()}</span>.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              The link works for one hour. Check your junk folder if it doesn't arrive.
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => mut.reset()}>
              Use a different address
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-3">
            <label htmlFor="portal-email" className="block text-sm font-medium text-foreground">
              Email address
            </label>
            <Input
              id="portal-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              disabled={mut.isPending}
              required
            />
            {mut.error && (
              <p className="text-sm text-rose-600">{(mut.error as Error).message}</p>
            )}
            {/*
              ⚠️⚠️ THE PAGE'S ONE VOLT ELEMENT (rule 4). Volt is spent once per page,
              and on the report that is the hero figure — here there is nothing else
              competing, and the only thing a visitor can do is this.

              ⚠️ INK ON VOLT, NOT WHITE: `--rpt-volt` is a light yellow-green, so
              white on it is unreadable. The same pairing rule the kelly nav marker
              follows.
            */}
            <Button
              type="submit"
              disabled={mut.isPending || !email.trim()}
              className="psh-door-cta w-full"
            >
              {mut.isPending ? 'Sending…' : 'Email me a sign-in link'}
            </Button>
          </form>
        )}

        {/*
          ⚠️⚠️ IT USED TO SAY "Contact your Kyck account manager", WHICH IS USELESS TO
          SOMEONE WHO CANNOT GET IN TO FIND OUT WHO THAT IS. A number and an address
          are reachable without an account.

          ⚠️ BOTH VERIFIED FROM kyck's OWN CONTENT FILE rather than invented
          (`kyck-site-2/src/content/site.ts`): 1300 726 992 / hello@kyck.com.au. A
          wrong number on a login page is a client who cannot reach anybody.

          ⚠️ `tel:` WITH THE SPACING REMOVED, which is what it wants; the visible
          text keeps the spacing, which is what a reader wants.
        */}
        <p className="mt-8 text-xs text-muted-foreground">
          Trouble signing in? Call{' '}
          <a className="underline underline-offset-2" href="tel:1300726992">
            1300 726 992
          </a>{' '}
          or email{' '}
          <a className="underline underline-offset-2" href="mailto:hello@kyck.com.au">
            hello@kyck.com.au
          </a>
          .
        </p>
      </div>
    </PortalShell>
  );
}
